import React, { useEffect, useState } from 'react';
import { Film, Loader2 } from 'lucide-react';
import { BrandSponsorCandidate, getCreatorSponsorCandidates } from '../../api/payment/brand-deal.api';
import { apiErrorMessage } from '../../utils/api-error-message';
import { EXISTING_POST_FORM_NOTE, NO_CANDIDATES_MESSAGE, excerpt } from '../../utils/brand-deal-content';

export type BrandContentSource = 'new' | 'existing';

const SOURCE_LABELS: Record<BrandContentSource, string> = {
  new: 'Nội dung mới',
  existing: 'Dùng bài có sẵn của creator',
};

/** Hai lựa chọn nguồn nội dung của lời mời (spec 2026-10-12 §5.2). */
export const ContentSourceToggle: React.FC<{ value: BrandContentSource; onChange: (v: BrandContentSource) => void }> = ({
  value,
  onChange,
}) => (
  <div role="radiogroup" aria-label="Nguồn nội dung" className="flex flex-wrap gap-2">
    {(Object.keys(SOURCE_LABELS) as BrandContentSource[]).map((key) => (
      <button
        key={key}
        type="button"
        role="radio"
        aria-checked={value === key}
        onClick={() => onChange(key)}
        className={`rounded-full border px-3 py-1 text-xs font-semibold ${
          value === key ? 'border-[#0057FF] bg-[#0057FF] text-white' : 'ui-divider ui-text'
        }`}
      >
        {SOURCE_LABELS[key]}
      </button>
    ))}
  </div>
);

const formatDay = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const CandidateThumb: React.FC<{ candidate: BrandSponsorCandidate }> = ({ candidate }) => {
  const image = candidate.images[0];
  if (image) return <img src={image.url} alt="" className="size-16 shrink-0 rounded-md object-cover" />;
  return (
    <span className="flex size-16 shrink-0 items-center justify-center rounded-md bg-slate-100 dark:bg-slate-800">
      {candidate.videos.length > 0 && <Film size={18} className="ui-muted" />}
    </span>
  );
};

export interface BrandExistingPostPickerProps {
  campaignId: string;
  creatorId: string;
  selectedPostId: string | null;
  onSelect: (candidate: BrandSponsorCandidate) => void;
}

/** Bài công khai của creator hợp định dạng chiến dịch, 12 bài mỗi lần tải, bấm "Xem thêm" để tải tiếp. */
const BrandExistingPostPicker: React.FC<BrandExistingPostPickerProps> = ({ campaignId, creatorId, selectedPostId, onSelect }) => {
  const [items, setItems] = useState<BrandSponsorCandidate[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getCreatorSponsorCandidates(campaignId, creatorId, page)
      .then((res) => {
        if (!alive) return;
        setItems((prev) => (page === 1 ? res.items : [...prev, ...res.items]));
        setHasMore(res.hasMore);
      })
      .catch((err) => {
        if (alive) setError(apiErrorMessage(err, 'Chưa tải được bài của creator, bạn thử lại sau ít phút.'));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [campaignId, creatorId, page]);

  return (
    <div className="space-y-2">
      <p className="text-[12px] ui-muted">{EXISTING_POST_FORM_NOTE}</p>
      {error && <p className="text-[12px] text-rose-600 dark:text-rose-400">{error}</p>}
      {!loading && !error && items.length === 0 && <p className="text-[12px] ui-muted">{NO_CANDIDATES_MESSAGE}</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((c) => (
          <button
            key={c.postId}
            type="button"
            aria-pressed={selectedPostId === c.postId}
            onClick={() => onSelect(c)}
            className={`flex gap-2 rounded-md border p-2 text-left ${
              selectedPostId === c.postId ? 'border-[#0057FF] bg-blue-50 dark:bg-blue-900/20' : 'ui-divider'
            }`}
          >
            <CandidateThumb candidate={c} />
            <span className="min-w-0 flex-1">
              <span className="block text-[12px] ui-text">{excerpt(c.content, 120) || 'Bài không có chữ'}</span>
              <span className="block text-[11px] ui-muted">Đăng ngày {formatDay(c.createdAt)}</span>
            </span>
          </button>
        ))}
      </div>
      {loading && <Loader2 size={16} className="animate-spin ui-muted" />}
      {!loading && hasMore && (
        <button type="button" onClick={() => setPage((p) => p + 1)} className="text-[12px] font-semibold text-[#0057FF]">
          Xem thêm
        </button>
      )}
    </div>
  );
};

export default BrandExistingPostPicker;
